import { useState, useRef } from 'react';
import api from '../services/api';
import {
  LuDownloadCloud, LuUploadCloud, LuShieldCheck, LuAlertTriangle, LuCheckCircle2, LuFileDown, LuFileUp, LuTrash2,
} from 'react-icons/lu';

const TABLE_LABELS = {
  departments:      'Departamentos',
  employees:        'Funcionários',
  users:            'Usuários',
  indicator_configs:'Modelos de Avaliação',
  assessments:      'Avaliações',
  time_records:     'Registros de Ponto',
};

function SummaryTable({ summary, title }) {
  const total = Object.values(summary).reduce((a, b) => a + b, 0);
  return (
    <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, padding: 16, marginTop: 12 }}>
      {title && <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-1)', marginBottom: 10 }}>{title}</div>}
      <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <tbody>
          {Object.entries(summary).map(([key, count]) => (
            <tr key={key} style={{ borderBottom: '1px solid var(--border)' }}>
              <td style={{ padding: '5px 8px', color: 'var(--text-2)' }}>{TABLE_LABELS[key] || key}</td>
              <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 700, color: 'var(--text-1)' }}>
                {count} {count === 1 ? 'registro' : 'registros'}
              </td>
            </tr>
          ))}
          <tr>
            <td style={{ padding: '7px 8px', fontWeight: 700, color: 'var(--text-1)', borderTop: '2px solid var(--border)' }}>Total</td>
            <td style={{ padding: '7px 8px', textAlign: 'right', fontWeight: 800, color: 'var(--accent)', borderTop: '2px solid var(--border)' }}>{total}</td>
          </tr>
        </tbody>
      </table>
      </div>
    </div>
  );
}

/* ─── Seção de Export ─── */
function ExportSection() {
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');
  const [lastExport, setLastExport] = useState(null); // { filename, summary, at }

  const handleExport = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/backup/export');
      const filename = `dhr-semaforo-backup-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      setLastExport({ filename, summary: data.summary, at: data.created_at });
    } catch (e) {
      setError(e.response?.data?.error || 'Erro ao gerar backup');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={card}>
      <div style={cardHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={iconCircle('var(--success-soft)', 'var(--success)')}><LuFileDown size={20} /></div>
          <div>
            <div style={cardTitle}>Exportar Backup</div>
            <div style={cardSub}>Gera um arquivo JSON com todos os dados do sistema</div>
          </div>
        </div>
      </div>

      <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 16, lineHeight: 1.6 }}>
        O backup inclui: departamentos, funcionários, usuários, modelos de avaliação, avaliações e
        registros de ponto. O arquivo gerado pode ser usado para restaurar o sistema.
      </div>

      {error && <div className="alert-custom danger" style={{ marginBottom: 12, fontSize: 13 }}>{error}</div>}

      {lastExport && (
        <div style={{ background: 'var(--success-soft)', border: '1px solid var(--success-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--success)', fontWeight: 700, marginBottom: 4 }}>
            <LuCheckCircle2 /> Backup gerado com sucesso
          </div>
          <div style={{ color: 'var(--text-2)' }}>
            Arquivo: <strong>{lastExport.filename}</strong><br />
            Gerado em: {new Date(lastExport.at).toLocaleString('pt-BR')}
          </div>
          <SummaryTable summary={lastExport.summary} />
        </div>
      )}

      <button onClick={handleExport} disabled={loading} style={btnPrimary}>
        <LuDownloadCloud size={16} />
        {loading ? 'Gerando backup…' : 'Baixar Backup Agora'}
      </button>
    </div>
  );
}

/* ─── Seção de Import ─── */
function ImportSection() {
  const fileRef  = useRef(null);
  const [file,        setFile]        = useState(null);   // File object
  const [preview,     setPreview]     = useState(null);   // parsed backup preview
  const [parsed,      setParsed]      = useState(null);   // full parsed JSON
  const [step,        setStep]        = useState('idle'); // idle | preview | confirm | importing | done | error
  const [result,      setResult]      = useState(null);
  const [error,       setError]       = useState('');

  const reset = () => {
    setFile(null); setPreview(null); setParsed(null);
    setStep('idle'); setResult(null); setError('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.endsWith('.json')) {
      setError('Selecione um arquivo .json válido'); return;
    }
    setFile(f);
    setError('');
    setStep('reading');

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = JSON.parse(evt.target.result);
        if (!data.version || !data.tables || !['DHR Gestão', 'DHR Semáforo'].includes(data.system)) {
          setError('Arquivo inválido: não é um backup do DHR Gestão');
          setStep('idle');
          return;
        }
        setParsed(data);
        setPreview({
          version:    data.version,
          system:     data.system,
          created_at: data.created_at,
          summary:    data.summary || Object.fromEntries(
            Object.entries(data.tables).map(([k, v]) => [k, Array.isArray(v) ? v.length : 0])
          ),
        });
        setStep('preview');
      } catch {
        setError('Não foi possível ler o arquivo. Verifique se é um JSON válido.');
        setStep('idle');
      }
    };
    reader.readAsText(f);
  };

  const handleImport = async () => {
    if (!parsed) return;
    setStep('importing');
    setError('');
    try {
      const { data } = await api.post('/backup/import', parsed);
      setResult(data);
      setStep('done');
    } catch (e) {
      setError(e.response?.data?.error || 'Erro ao importar backup');
      setStep('preview');
    }
  };

  return (
    <div style={card}>
      <div style={cardHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={iconCircle('var(--warning-soft)', 'var(--warning)')}><LuFileUp size={20} /></div>
          <div>
            <div style={cardTitle}>Importar Backup</div>
            <div style={cardSub}>Restaura todos os dados a partir de um arquivo de backup</div>
          </div>
        </div>
        {step !== 'idle' && (
          <button onClick={reset} title="Cancelar" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)' }}>
            <LuTrash2 size={15} />
          </button>
        )}
      </div>

      {/* Aviso */}
      <div style={{ display: 'flex', gap: 10, background: 'var(--warning-soft)', border: '1px solid var(--warning-border)', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
        <LuAlertTriangle size={18} color="var(--warning)" style={{ flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: 13, color: 'var(--warning)', lineHeight: 1.6 }}>
          <strong>Atenção:</strong> a importação substitui <strong>todos os dados atuais</strong> do sistema
          pelos dados do arquivo. Esta operação não pode ser desfeita. Gere um backup atual antes de prosseguir.
        </div>
      </div>

      {error && <div className="alert-custom danger" style={{ marginBottom: 12, fontSize: 13 }}>{error}</div>}

      {/* IDLE: escolher arquivo */}
      {(step === 'idle' || step === 'reading') && (
        <div>
          <input
            ref={fileRef}
            type="file"
            accept=".json"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <button onClick={() => fileRef.current?.click()} disabled={step === 'reading'} style={btnWarning}>
            <LuUploadCloud size={16} />
            {step === 'reading' ? 'Lendo arquivo…' : 'Selecionar Arquivo de Backup'}
          </button>
        </div>
      )}

      {/* PREVIEW: dados do arquivo */}
      {step === 'preview' && preview && (
        <div>
          <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-1)', marginBottom: 6 }}>
            Arquivo selecionado: <span style={{ color: 'var(--text-2)', fontWeight: 400 }}>{file?.name}</span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 2 }}>
            Sistema: <strong style={{ color: 'var(--text-1)' }}>{preview.system}</strong>
            {' · '}Versão: <strong style={{ color: 'var(--text-1)' }}>{preview.version}</strong>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 8 }}>
            Gerado em: <strong style={{ color: 'var(--text-1)' }}>{new Date(preview.created_at).toLocaleString('pt-BR')}</strong>
          </div>

          <SummaryTable summary={preview.summary} title="Dados que serão importados:" />

          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button onClick={reset} style={btnCancel}>Cancelar</button>
            <button onClick={() => setStep('confirm')} style={btnDanger}>
              <LuAlertTriangle size={14} />
              Confirmar Restauração
            </button>
          </div>
        </div>
      )}

      {/* CONFIRM: confirmação final */}
      {step === 'confirm' && (
        <div style={{ background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', borderRadius: 8, padding: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--danger)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            <LuAlertTriangle /> Confirmação final
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 16, lineHeight: 1.6 }}>
            Você está prestes a <strong>apagar todos os dados atuais</strong> e restaurar o backup de{' '}
            <strong>{new Date(preview.created_at).toLocaleString('pt-BR')}</strong>.
            <br />Deseja continuar?
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setStep('preview')} style={btnCancel}>Voltar</button>
            <button onClick={handleImport} style={{ ...btnDanger, fontWeight: 800 }}>
              Sim, restaurar agora
            </button>
          </div>
        </div>
      )}

      {/* IMPORTING */}
      {step === 'importing' && (
        <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-3)', fontSize: 14 }}>
          <div style={{ marginBottom: 10, fontSize: 28 }}>⏳</div>
          Importando dados… aguarde.
        </div>
      )}

      {/* DONE */}
      {step === 'done' && result && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--success)', fontWeight: 700, fontSize: 15, marginBottom: 8 }}>
            <LuCheckCircle2 size={20} /> Backup restaurado com sucesso!
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 4 }}>
            Restaurado em: {new Date(result.imported_at).toLocaleString('pt-BR')}
          </div>
          <SummaryTable summary={result.summary} title="Dados importados:" />
          <button onClick={reset} style={{ ...btnPrimary, marginTop: 14 }}>
            Nova Importação
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── Página principal ─── */
export default function BackupPage() {
  return (
    <div className="page-card">
      <div className="page-card-header">
        <div>
          <div className="page-card-title">Backup do Sistema</div>
          <div className="page-card-subtitle">Exporte ou restaure todos os dados do DHR Gestão</div>
        </div>
        <LuShieldCheck size={28} color="var(--accent)" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(340px, 100%), 1fr))', gap: 20 }}>
        <ExportSection />
        <ImportSection />
      </div>
    </div>
  );
}

/* ─── Estilos ─── */
const card = {
  border: '1px solid var(--border)', borderRadius: 12, padding: 24, background: 'var(--surface-1)',
};
const cardHeader = {
  display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
  marginBottom: 16, gap: 12,
};
const cardTitle = { fontWeight: 700, fontSize: 15, color: 'var(--text-1)' };
const cardSub   = { fontSize: 12, color: 'var(--text-3)', marginTop: 2 };

const iconCircle = (bg, color) => ({
  width: 42, height: 42, borderRadius: '50%', background: bg,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  color, flexShrink: 0,
});

const btnBase = {
  padding: '10px 20px', border: 'none', borderRadius: 8, cursor: 'pointer',
  fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
  display: 'inline-flex', alignItems: 'center', gap: 8, transition: 'opacity 0.15s',
};
const btnPrimary = { ...btnBase, background: 'var(--accent)', color: 'var(--on-accent)' };
const btnWarning = { ...btnBase, background: 'var(--warning-soft)', color: 'var(--warning)', border: '1px solid var(--warning-border)' };
const btnDanger  = { ...btnBase, background: 'var(--danger)', color: 'var(--on-solid)' };
const btnCancel  = { ...btnBase, background: 'var(--surface-1)', color: 'var(--text-2)', border: '1px solid var(--border)', fontWeight: 400 };
